import { Box, Typography, useTheme } from "@mui/material";
import i18next from "i18next";

import { formatNumber } from "src/utils/formatting";

type DiffPercentageTagProps = {
    value: number;
    position?: "right" | "bottom";
};

export const DiffPercentageTag = (props: DiffPercentageTagProps) => {
    const { value, position = "right" } = props;
    const theme = useTheme();
    const positive = value >= 0;
    const isNeutral = value === 0;
    const accentColor =
        isNeutral ? theme.custom.grey[1500]
        : !positive ? theme.custom.green[500]
        : theme.custom.red[500];
    const bgColor =
        isNeutral ? theme.custom.grey[300]
        : !positive ? theme.custom.green[100]
        : theme.custom.red[100];
    const isRight = position === "right";

    if (isNaN(value) || value === Infinity || value === -Infinity) {
        return <></>;
    }

    return (
        <Box
            sx={{
                display: "inline-flex",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: bgColor,
                border: `2px solid ${accentColor}`,
                borderRight: isRight ? "none" : undefined,
                borderRadius: theme.spacing(0.75),
                padding: theme.spacing(0.25, 0.75),
                paddingRight: theme.spacing(1),
                height: "26px",
                zIndex: 2,
            }}
        >
            {isRight ?
                <Box
                    sx={{
                        background: `linear-gradient(45deg, transparent 0%, transparent 55%, ${bgColor} 55%, ${bgColor} 100%)`,
                        position: "absolute",
                        border: `2px solid ${accentColor}`,
                        borderLeft: "none",
                        borderBottom: "none",
                        borderRadius: theme.spacing(0.75),
                        borderTopRightRadius: theme.spacing(0.5),
                        borderBottomRightRadius: theme.spacing(0.25),
                        borderTopLeftRadius: theme.spacing(0.25),
                        height: "18px",
                        width: "18px",
                        right: -7,
                        top: 2,
                        transform: "rotate(45deg)",
                        zIndex: -2,
                    }}
                />
            :   <Box
                    sx={{
                        background: `linear-gradient(135deg, transparent 0%, transparent 45%, ${bgColor} 45%, ${bgColor} 100%)`,
                        position: "absolute",
                        border: `2px solid ${accentColor}`,
                        borderTop: "none",
                        borderLeft: "none",
                        borderRadius: "1.5px",
                        height: "12px",
                        width: "12px",
                        bottom: -7,
                        left: "50%",
                        transform: "translateX(-50%) rotate(45deg)",
                        zIndex: -2,
                    }}
                />
            }
            <Typography
                component="span"
                variant="body2"
                fontWeight="bold"
                sx={{
                    fontSize: "0.75rem",
                    lineHeight: 1,
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <Box component="span" sx={{ color: accentColor }}>
                    {isNeutral ?
                        "="
                    : positive ?
                        "+"
                    :   "-"}{" "}
                </Box>
                <Box component="span" sx={{ color: "text.primary" }}>
                    {formatNumber(Math.abs(value / 100), i18next.language, {
                        maximumFractionDigits: 2,
                        style: "percent",
                        notation: "compact",
                    })}
                </Box>
            </Typography>
        </Box>
    );
};

export default DiffPercentageTag;
